import type { NegotiationVersion } from '~/features/provider-hlx/job-opportunities/types';
import type {
  NegotiationHistoryLatest,
  NegotiationHistoryVersion,
  NegotiationTermGroup,
} from '~/features/shared/market-content/NegotiationHistory';
import { negotiationParty } from '~/features/shared/market-content/NegotiationHistory';
import type { B2BNegotiationChainRow } from '../job-opportunities/types';
import { formatCurrency, formatDate } from '../job-opportunities/utils';
import type { ProviderJobNegotiationChainRow } from '../job-responses/provider-responses/types';
import { htmlToText, SERVICE_TERM_TABS, serviceTermLabel, type ServiceTerm } from './termDisplay';

/** Every negotiation term the history can show, grouped like the counteroffer tabs. */
export const JOB_TERM_GROUPS: readonly NegotiationTermGroup[] = [
  ...SERVICE_TERM_TABS.flatMap((tab) =>
    tab.sections.map((section) => ({ title: section.title, terms: section.terms }))
  ),
  { title: 'Contract Value', terms: ['totalContractValue', 'avgHourlyRate'] },
];

/** B2B rounds are priced per FTE, so the staffing group leads with those terms. */
export const MSAAS_TERM_GROUPS: readonly NegotiationTermGroup[] = JOB_TERM_GROUPS.map((group) =>
  group.title === 'Staffing Requirements'
    ? { ...group, terms: ['numOfFtes', 'fteShiftsPerYear', 'yearlyRvusPerFte', ...group.terms] }
    : group
);

const LABELS: Record<string, string> = {
  numOfFtes: 'FTEs',
  fteShiftsPerYear: 'Shifts per FTE per year',
  yearlyRvusPerFte: 'Yearly RVUs per FTE',
  totalContractValue: 'Total contract value',
  avgHourlyRate: 'Average hourly rate',
};
export const negotiationHistoryTermLabel = (key: string) =>
  LABELS[key] ?? serviceTermLabel(key as ServiceTerm);

const MONEY_KEYS = new Set([
  'pricePerHour',
  'pricePerShift',
  'pricePerRvu',
  'pricePerCallShift',
  'rvuBonusAmt',
  'totalContractValue',
  'avgHourlyRate',
]);

/** Exact dollars: whole amounts without cents, anything else to the cent. Non-numbers print as sent. */
export const formatMoney = (value: string | number) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  const digits = Number.isInteger(amount) ? 0 : 2;
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

/**
 * [MCV-1794] A rate in its unit, e.g. `$52.37/RVU`, `$100.50/hr`, `$1,200/shift`. The cents stay:
 * rounding to whole dollars made two rounds at $52.37 and $52.80 look identical in the history,
 * hiding the very change a counteroffer is about. Null when there is no usable amount.
 */
export const formatRate = (amount: unknown, unit: string): string | null => {
  if (amount == null || amount === '') return null;
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? `${formatMoney(parsed)}/${unit}` : null;
};

/** A term as the history prints it: plain text, with no raw enum codes or markup. */
export const formatNegotiationHistoryTerm = (key: string, value: unknown): string => {
  if (value == null) return '';
  const text = String(value);
  if (text.trim() === '') return '';
  if (MONEY_KEYS.has(key)) return formatMoney(text);
  if (key.endsWith('Date')) return formatDate(text);
  if (key.endsWith('Txt')) return htmlToText(text);
  return /^[A-Z][A-Z_]*$/.test(text) ? enumLabel(text) : text;
};

const ACRONYMS = new Set(['RVU', 'FTE']);
const enumLabel = (code: string) =>
  code
    .split('_')
    .map((word) => (ACRONYMS.has(word) ? word : word.charAt(0) + word.slice(1).toLowerCase()))
    .join(' ');

const RATE_TERMS = {
  HOUR: { key: 'pricePerHour', unit: 'hr' },
  SHIFT: { key: 'pricePerShift', unit: 'shift' },
  RVU: { key: 'pricePerRvu', unit: 'RVU' },
} as const;

/** The headline rate in the round's own compensation unit, or null when it has none. */
export const headlineRate = (terms: Record<string, unknown>): string | null => {
  const comp = RATE_TERMS[terms.compType as keyof typeof RATE_TERMS] ?? RATE_TERMS.HOUR;
  return formatRate(terms[comp.key], comp.unit);
};

export const headlineValue = (value: string | number | null | undefined): string | null => {
  const formatted = formatCurrency(value == null ? null : String(value));
  return formatted === '—' ? null : formatted;
};

const count = (value: unknown) => (value == null || value === '' ? null : String(value));

const rowKey = (row: { id?: number; version?: number | null }, index: number) =>
  row.id ?? row.version ?? `idx-${index}`;

/** Job negotiation chain rows (Job Responses): the viewer is the employer. */
export const jobChainToVersions = (
  rows: ProviderJobNegotiationChainRow[]
): NegotiationHistoryVersion[] =>
  rows.map((row, index) => ({
    id: rowKey(row, index),
    version: row.version ?? null,
    sender: row.sender ?? null,
    createdAt: row.createdAt ?? null,
    headline: {
      shifts: count(row.numOfShifts),
      rate: headlineRate(row),
      value: headlineValue(row.totalContractValue),
    },
    terms: row,
  }));

/** MSaaS (B2B) chain rows; `senderParty` is RESPONDER or LISTING_OWNER. */
export const msaasChainToVersions = (rows: B2BNegotiationChainRow[]): NegotiationHistoryVersion[] =>
  rows.map((row, index) => ({
    id: rowKey(row, index),
    version: row.version ?? null,
    sender: typeof row.senderParty === 'string' ? row.senderParty : null,
    createdAt: row.createdAt ?? null,
    headline: {
      ftes: count(row.numOfFtes),
      rate: headlineRate(row),
      value: headlineValue(row.totalContractValue),
    },
    terms: row,
  }));

/** Provider-side job rounds: the API sends the headline figures only. */
export const providerJobToVersions = (rows: NegotiationVersion[]): NegotiationHistoryVersion[] =>
  rows.map((row) => ({
    id: row.version,
    version: row.version,
    sender: row.sender,
    createdAt: row.createdAt,
    headline: {
      shifts: count(row.numOfShifts),
      rate: formatRate(row.pricePerHour, 'hr'),
      value: headlineValue(row.totalContractValue),
    },
    terms: {},
  }));

export const latestOf = (
  latest: {
    version: number;
    headline: NegotiationHistoryLatest['headline'];
  } | null,
  sender: string | null | undefined,
  viewerSender: string
): NegotiationHistoryLatest | null =>
  latest ? { ...latest, party: negotiationParty(sender, viewerSender) } : null;
